/**
 * Iatrode UI Components Library - Main Entry Point
 * 包含所有核心组件和工具函数
 */

// 导入核心组件
// Tabs组件会自动初始化，无需手动import

/**
 * 全局消息容器管理器
 */
class MessageContainerManager {
    constructor() {
        this.containers = {};
        this.baseZIndex = 9999;
        this.currentZIndex = this.baseZIndex;
    }

    /**
     * 获取指定位置的容器
     */
    getContainer(position) {
        if (!this.containers[position]) {
            this.createContainer(position);
        }
        return this.containers[position];
    }

    /**
     * 创建指定位置的容器
     */
    createContainer(position) {
        if (this.containers[position]) return this.containers[position];

        // 确保 document.body 存在
        if (!document.body) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => {
                    this.createContainer(position);
                });
                return null;
            } else {
                console.warn('MessageContainer: document.body is not available');
                return null;
            }
        }

        const container = document.createElement('div');
        container.className = `message-container position-${position}`;
        container.setAttribute('data-position', position);
        // 外层容器允许可视溢出以显示阴影，但不用于滚动（避免裁切阴影）
        container.style.overflowX = 'visible';
        container.style.overflowY = 'visible';
        container.style.minWidth = '0';
        container.style.maxWidth = 'none';

        // 将容器放入一个全屏固定的 viewport，避免被页面其它布局或 transform 裁切
        let viewport = document.querySelector('.message-viewport');
        if (!viewport) {
            viewport = document.createElement('div');
            viewport.className = 'message-viewport';
            // 样式控制由 CSS 管理；运行时做些保护设置
            viewport.style.position = 'fixed';
            viewport.style.inset = '0';
            viewport.style.pointerEvents = 'none';
            viewport.style.zIndex = String(this.baseZIndex);
            document.body.appendChild(viewport);
        }

        // 把容器设为相对于 viewport 的绝对定位
        container.style.position = 'absolute';

        // 内部滚动包装器，真正处理纵向滚动行为（样式交由 CSS 控制，避免在 JS 中硬编码 85vh）
        const scroll = document.createElement('div');
        scroll.className = 'message-scroll';
        // 防止 scroll 自动拉伸占满容器，确保其尺寸由内容驱动
        scroll.style.flex = '0 0 auto';
        scroll.style.maxHeight = 'calc(100vh - 2rem)';
        // 初始状态不接收指针事件，避免空的滚动区域拦截页面点击
        scroll.style.pointerEvents = 'none';
        // 让内部滚动区域占据容器空间并启用纵向滚动（样式由 SCSS 管理）

        container.appendChild(scroll);
        viewport.appendChild(container);

        this.containers[position] = container;
        return container;
    }

    /**
     * 获取下一个 z-index 值
     */
    getNextZIndex() {
        return ++this.currentZIndex;
    }

    /**
     * 重置 z-index 计数器
     */
    resetZIndex() {
        this.currentZIndex = this.baseZIndex;
    }

    /**
     * 添加消息元素到指定位置的容器
     */
    addMessage(element, position = 'top-right') {
        const container = this.getContainer(position);
        if (!container) {
            console.warn(`MessageContainer: Failed to create container for position ${position}`);
            return false;
        }

        // 避免子元素在 flex 容器中被压扁或拉伸
        element.style.flex = '0 0 auto';
        element.style.minWidth = '0';

        // 包装一层 message-wrap，用于水平对齐并保持与视口边缘的间距
        const wrapper = document.createElement('div');
        wrapper.className = 'message-wrap';
        wrapper.style.display = 'flex';
        wrapper.style.width = 'auto';
        wrapper.style.pointerEvents = 'none'; // 让外层不可交互，内部 element 可交互

        // 让消息本身可以接收指针事件
        element.style.pointerEvents = 'auto';

        // 确保消息元素的点击不会冒泡到 viewport（避免拦截页面层的点击）
        const stopEvent = (e) => { e.stopPropagation(); };
        element.addEventListener('click', stopEvent);
        element.addEventListener('pointerdown', stopEvent);

        // 根据 position 设置 wrapper 的水平对齐（使用 margin 自动布局以避免占满整行导致点击被拦截）
        if (/right$/.test(position)) {
            wrapper.style.marginLeft = 'auto';
            wrapper.style.justifyContent = 'flex-end';
        } else if (/left$/.test(position)) {
            wrapper.style.marginRight = 'auto';
            wrapper.style.justifyContent = 'flex-start';
        } else {
            wrapper.style.margin = '0 auto';
            wrapper.style.justifyContent = 'center';
        }

        wrapper.appendChild(element);
        // 添加到内部滚动区域（.message-scroll）以保持外层 overflow 可见
        const scrollEl = container.querySelector('.message-scroll') || container;
        // 如果当前容器中没有其他消息，先重置位置/偏移，避免残留的 Y 偏移影响新的消息
        try {
            const childCount = scrollEl.children.length;
            if (childCount === 0) {
                // 清理可能的残留偏移
                try { this.resetPosition(position); } catch (e) { }
            }
        } catch (e) { }
        // 仅让 wrapper 本身接收指针事件，避免整个 scroll 区域拦截页面点击
        try { wrapper.style.pointerEvents = 'auto'; } catch (e) { }
        // 始终追加到 scroll 的末尾；top-* 的视觉顺序通过 CSS 的 column-reverse 控制，避免直接改变 DOM 插入顺序
        scrollEl.appendChild(wrapper);

        // 监听进入隐藏动画的时刻，将正在隐藏的 wrapper 切换为绝对定位，从文档流移除，避免影响后续布局
        const onPossibleHideStart = (ev) => {
            try {
                const isHiding = element.classList.contains('alert-hide') || element.classList.contains('hide');
                if (!isHiding) return;

                // 计算相对于容器的位置并将 wrapper 绝对定位
                const containerRect = container.getBoundingClientRect();
                const wrapRect = wrapper.getBoundingClientRect();
                const top = wrapRect.top - containerRect.top;
                const left = wrapRect.left - containerRect.left;

                // 应用绝对定位与固定宽度，移出文档流，并触发平滑过渡动画
                wrapper.classList.add('message-abs');
                wrapper.style.position = 'absolute';
                wrapper.style.top = top + 'px';
                wrapper.style.left = left + 'px';
                wrapper.style.width = wrapRect.width + 'px';
                // 初始使用微小的位移与半透明，下一帧回到原位以触发过渡
                wrapper.style.transform = 'translateY(6px)';
                wrapper.style.opacity = '0.95';
                wrapper.style.pointerEvents = 'none';
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        try {
                            wrapper.style.transform = 'translateY(0)';
                            wrapper.style.opacity = '';
                        } catch (e) { }
                    });
                });

                // 标记已处理，后续不重复设置
                wrapper.dataset._abs = '1';
            } catch (e) { }
        };

        // 注册动画/过渡开始监听，用于捕捉 hide 类触发的过渡
        element.addEventListener('animationstart', onPossibleHideStart);
        element.addEventListener('transitionstart', onPossibleHideStart);
        // 记录引用以便后续在 removeMessage 中清理
        wrapper._onPossibleHideStart = onPossibleHideStart;
        return true;
    }

    /**
     * 从容器中移除消息元素
     */
    removeMessage(element) {
        if (!element) return;
        // 如果元素被包裹在 .message-wrap 中，移除整个 wrapper
        const wrapper = element.closest('.message-wrap');
        // 如果之前注册了隐藏监听器，先移除监听器
        try {
            if (wrapper && wrapper._onPossibleHideStart) {
                element.removeEventListener('animationstart', wrapper._onPossibleHideStart);
                element.removeEventListener('transitionstart', wrapper._onPossibleHideStart);
                delete wrapper._onPossibleHideStart;
            }
        } catch (e) { }

        // 清理可能残留的内联样式（transform/position/transition）
        try {
            const inner = wrapper ? wrapper.querySelector('.message, .alert, .toast') : element;
            if (inner) {
                inner.style.transform = '';
                inner.style.transition = '';
                inner.style.position = '';
                inner.style.top = '';
                inner.style.left = '';
                inner.style.width = '';
            }
            if (wrapper) {
                wrapper.style.position = '';
                wrapper.style.top = '';
                wrapper.style.left = '';
                wrapper.style.width = '';
                wrapper.style.pointerEvents = '';
            }
        } catch (e) { }

        if (wrapper && wrapper.parentNode) {
            const parentScroll = wrapper.parentNode;
            try {
                // 先测量兄弟元素位置（移除前）
                const siblings = Array.from(parentScroll.children).filter(c => c !== wrapper);
                const beforeTops = siblings.map(s => s.getBoundingClientRect().top);

                // 从 DOM 中移除 wrapper（触发布局变化）
                parentScroll.removeChild(wrapper);

                // 如果现在没有子项，恢复 pointer-events 为 none，允许页面下层点击透传
                try {
                    if (parentScroll.children.length === 0) parentScroll.style.pointerEvents = 'none';
                } catch (e) { }

                // 对剩余的兄弟元素做平滑上移动画
                requestAnimationFrame(() => {
                    try {
                        const afterTops = siblings.map(s => s.getBoundingClientRect().top);
                        siblings.forEach((s, i) => {
                            try {
                                const delta = beforeTops[i] - afterTops[i];
                                if (!delta) return;
                                // 先把元素瞬间移动到旧位置（通过 transform），然后过渡到自然位置
                                s.style.transition = 'transform 0.28s ease';
                                s.style.transform = `translateY(${delta}px)`;
                                // 强制重绘后触发回到0的过渡
                                s.getBoundingClientRect();
                                requestAnimationFrame(() => {
                                    try { s.style.transform = ''; } catch (e) { }
                                });
                                // 清理函数
                                const clean = () => {
                                    try { s.style.transition = ''; s.style.transform = ''; } catch (e) { }
                                    s.removeEventListener('transitionend', clean);
                                };
                                s.addEventListener('transitionend', clean);
                            } catch (e) { }
                        });
                    } catch (e) { }
                });
            } catch (e) {
                // fallback: 直接移除
                try { parentScroll.removeChild(wrapper); } catch (e) { }
            }
        } else if (element.parentNode) {
            const parent = element.parentNode;
            parent.removeChild(element);
            try {
                if (parent.children.length === 0) parent.style.pointerEvents = 'none';
            } catch (e) { }
        }
    }

    /**
     * 清理空容器
     */
    cleanup() {
        Object.keys(this.containers).forEach(position => {
            const container = this.containers[position];
            if (!container) return;
            const scroll = container.querySelector('.message-scroll');
            const hasChildren = scroll ? scroll.children.length > 0 : container.children.length > 0;
            if (!hasChildren) {
                // 如果没有消息，先重置滚动位置（避免残留的 Y 偏移）
                if (scroll) {
                    try { scroll.scrollTop = 0; } catch (e) { }
                    try { scroll.style.pointerEvents = 'none'; } catch (e) { }
                }
                // 移除容器
                if (container.parentNode) container.parentNode.removeChild(container);
                delete this.containers[position];
            }
        });
    }

    /**
     * 重置指定位置容器的偏移与残留样式
     */
    resetPosition(position) {
        const container = this.containers[position];
        if (!container) return;
        const scroll = container.querySelector('.message-scroll');
        // 重置滚动位置
        if (scroll) {
            try { scroll.scrollTop = 0; } catch (e) { }
        }
        // 清除任何残留在子元素上的 transform/position，防止影响布局
        const children = scroll ? Array.from(scroll.children) : Array.from(container.children);
        children.forEach(wrap => {
            try {
                const inner = wrap.querySelector('.message, .alert, .toast') || wrap;
                if (inner) {
                    inner.style.transform = '';
                    inner.style.transition = '';
                    inner.style.position = '';
                    inner.style.top = '';
                    inner.style.left = '';
                    inner.style.width = '';
                }
            } catch (e) { }
        });
    }
}

// 全局消息容器管理器实例
const messageContainerManager = new MessageContainerManager();
// 将消息容器管理器暴露到全局，供其他组件使用
window.messageContainerManager = messageContainerManager;

/**
 * MethodOptionComponent 方法选择组件类
 * 提供方法选择功能
 */
/**
 * FieldSelectComponent 下拉选择组件（精简实现）
 * 提供绑定 .field-select、打开/关闭、选中值、键盘导航等功能
 */
class FieldSelectComponent {
    constructor() {
        this.selects = new Map();
        this.init();
    }

    init() {
        this.bindAll();
        this.observeNewElements();
    }

    bindAll() {
        const selects = document.querySelectorAll('.field-select');
        selects.forEach(sel => {
            if (!this.selects.has(sel)) this.bindSelect(sel);
        });
    }

    bindSelect(selectElement) {
        const options = Array.from(selectElement.querySelectorAll('.field-select-option'));
        const valueDisplay = selectElement.querySelector('.field-select-value') || selectElement.querySelector('.value');
        const data = {
            element: selectElement,
            options: options,
            valueDisplay: valueDisplay || { textContent: '' },
            isOpen: false
        };

        // store
        this.selects.set(selectElement, data);

        // click on select toggles
        selectElement.addEventListener('click', (e) => {
            // ignore clicks on option elements themselves
            const opt = e.target.closest('.field-select-option');
            if (opt) {
                this.selectOption(selectElement, opt);
                return;
            }
            this.toggle(selectElement);
        });

        // keyboard
        selectElement.addEventListener('keydown', (e) => this.handleKeydown(selectElement, e));
    }

    toggle(selectElement) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return;
        if (selectData.isOpen) {
            this.close(selectElement);
        } else {
            this.closeAllExcept(selectElement);
            this.open(selectElement);
        }
    }

    open(selectElement) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return;
        selectData.element.classList.add('active');
        selectData.isOpen = true;
        selectData.element.dispatchEvent(new CustomEvent('fieldselect:open'));
    }

    close(selectElement) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return;
        selectData.element.classList.remove('active');
        selectData.isOpen = false;
        selectData.element.dispatchEvent(new CustomEvent('fieldselect:close'));
    }

    closeAllExcept(exceptElement) {
        this.selects.forEach((selectData, selectElement) => {
            if (selectElement !== exceptElement && selectData.isOpen) this.close(selectElement);
        });
    }

    selectOption(selectElement, option) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return;
        const value = option.getAttribute('data-value');
        const text = option.textContent;
        if (selectData.valueDisplay) selectData.valueDisplay.textContent = text;
        selectData.options.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
        this.close(selectElement);
        selectData.element.dispatchEvent(new CustomEvent('fieldselect:change', { detail: { value, text, option } }));
    }

    handleKeydown(selectElement, e) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return;
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault(); this.toggle(selectElement); break;
            case 'Escape':
                e.preventDefault(); this.close(selectElement); break;
            case 'ArrowDown':
                e.preventDefault(); if (!selectData.isOpen) this.open(selectElement); else this.navigateOptions(selectElement, 1); break;
            case 'ArrowUp':
                e.preventDefault(); if (selectData.isOpen) this.navigateOptions(selectElement, -1); break;
        }
    }

    navigateOptions(selectElement, direction) {
        const selectData = this.selects.get(selectElement);
        if (!selectData || !selectData.isOpen) return;
        const options = Array.from(selectData.options);
        const current = options.find(o => o.classList.contains('selected'));
        const idx = current ? options.indexOf(current) : -1;
        let ni = idx + direction;
        if (ni < 0) ni = options.length - 1;
        if (ni >= options.length) ni = 0;
        if (options[ni]) this.selectOption(selectElement, options[ni]);
    }

    observeNewElements() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach(m => {
                m.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const selects = node.querySelectorAll ? node.querySelectorAll('.field-select') : [];
                        selects.forEach(s => { if (!this.selects.has(s)) this.bindSelect(s); });
                        if (node.classList && node.classList.contains('field-select') && !this.selects.has(node)) this.bindSelect(node);
                    }
                });
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    getValue(selectElement) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return null;
        const sel = selectData.element.querySelector('.field-select-option.selected');
        return sel ? { value: sel.getAttribute('data-value'), text: sel.textContent } : null;
    }

    setValue(selectElement, value) {
        const selectData = this.selects.get(selectElement);
        if (!selectData) return false;
        const opt = selectData.element.querySelector(`[data-value="${value}"]`);
        if (opt) { this.selectOption(selectElement, opt); return true; }
        return false;
    }
}

class MethodOptionComponent {
    constructor() {
        this.init();
    }

    init() {
        this.bindOptions();
        this.observeNewElements();
    }

    bindOptions() {
        const options = document.querySelectorAll('.method-option:not(.disabled)');
        options.forEach(option => {
            if (!option.hasAttribute('data-method-bound')) {
                this.bindOption(option);
                option.setAttribute('data-method-bound', 'true');
            }
        });
    }

    bindOption(option) {
        option.addEventListener('click', (e) => {
            const allOptions = document.querySelectorAll('.method-option:not(.disabled)');

            // 移除其他选中状态
            allOptions.forEach(opt => opt.classList.remove('selected'));

            // 添加当前选中状态
            option.classList.add('selected');

            // 触发选择事件
            option.dispatchEvent(new CustomEvent('methodoption:select', {
                detail: {
                    value: option.getAttribute('data-value') || option.textContent,
                    element: option
                }
            }));
        });
    }

    observeNewElements() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const options = node.querySelectorAll ? node.querySelectorAll('.method-option:not(.disabled)') : [];
                        options.forEach(option => {
                            if (!option.hasAttribute('data-method-bound')) {
                                this.bindOption(option);
                                option.setAttribute('data-method-bound', 'true');
                            }
                        });

                        if (node.classList && node.classList.contains('method-option') &&
                            !node.classList.contains('disabled') && !node.hasAttribute('data-method-bound')) {
                            this.bindOption(node);
                            node.setAttribute('data-method-bound', 'true');
                        }
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

/**
 * LoadingButtonComponent 加载状态按钮组件类
 * 提供按钮加载状态功能
 */
class LoadingButtonComponent {
    constructor() {
        this.init();
    }

    init() {
        this.bindButtons();
        this.observeNewElements();
    }

    bindButtons() {
        const buttons = document.querySelectorAll('.interactive-bt, [data-loading]');
        buttons.forEach(button => {
            if (!button.hasAttribute('data-loading-bound')) {
                this.bindButton(button);
                button.setAttribute('data-loading-bound', 'true');
            }
        });
    }

    bindButton(button) {
        button.addEventListener('click', (e) => {
            if (button.classList.contains('btn-loading') || button.classList.contains('loading')) {
                e.preventDefault();
                return;
            }

            // 检查是否有自定义的加载处理
            const customHandler = button.getAttribute('data-loading-handler');
            if (customHandler && window[customHandler]) {
                window[customHandler](button, e);
                return;
            }

            // 默认加载行为
            this.setLoading(button);

            // 默认 2 秒后恢复，可通过 data-loading-duration 自定义
            const duration = parseInt(button.getAttribute('data-loading-duration')) || 2000;
            setTimeout(() => {
                this.removeLoading(button);
            }, duration);
        });
    }

    setLoading(button, loadingText = '加载中...') {
        if (button.classList.contains('btn-loading')) return;

        // 如果没有包裹内容的元素，则用一个 span.btn-content 包裹当前内容，便于隐藏但保留布局
        try {
            if (!button.querySelector('.btn-content')) {
                const wrapper = document.createElement('span');
                wrapper.className = 'btn-content';
                // 把现有子节点移动到 wrapper 中
                while (button.firstChild) {
                    wrapper.appendChild(button.firstChild);
                }
                button.appendChild(wrapper);
            }
        } catch (e) {
            // 如果出错，降级为不包装，但仍继续设置 loading 类
        }

        // 保存原始 aria-busy 状态以便恢复（如果需要）
        if (!button.hasAttribute('data-original-aria-busy')) {
            const ab = button.getAttribute('aria-busy');
            button.setAttribute('data-original-aria-busy', ab === null ? '' : ab);
        }

        // 设置加载状态（不修改文本内容，使用 CSS 伪元素显示 spinner）
        // Use the `btn-loading` class for loading state
        button.classList.add('btn-loading');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        // 触发加载开始事件
        button.dispatchEvent(new CustomEvent('loadingbutton:start'));
    }

    removeLoading(button) {
        if (!button.classList.contains('btn-loading')) return;

        // 移除加载状态：仅移除 `btn-loading`
        button.classList.remove('btn-loading');
        button.disabled = false;

        // 恢复 aria-busy（如果之前存在）
        try {
            const orig = button.getAttribute('data-original-aria-busy');
            if (orig === '') {
                button.removeAttribute('aria-busy');
            } else if (orig === null) {
                button.removeAttribute('aria-busy');
            } else {
                button.setAttribute('aria-busy', orig);
            }
            button.removeAttribute('data-original-aria-busy');
        } catch (e) { }

        // 触发加载完成事件
        button.dispatchEvent(new CustomEvent('loadingbutton:complete'));
    }

    observeNewElements() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const buttons = node.querySelectorAll ? node.querySelectorAll('.interactive-bt, [data-loading]') : [];
                        buttons.forEach(button => {
                            if (!button.hasAttribute('data-loading-bound')) {
                                this.bindButton(button);
                                button.setAttribute('data-loading-bound', 'true');
                            }
                        });

                        if (node.classList && (node.classList.contains('interactive-bt') || node.hasAttribute('data-loading')) &&
                            !node.hasAttribute('data-loading-bound')) {
                            this.bindButton(node);
                            node.setAttribute('data-loading-bound', 'true');
                        }
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// Legacy global shims removed. Use Alert/Toast/FormInputUtils directly from their modules.
console.log('🎯 Iatrode UI Components Library Loaded');

/**
 * ThemeManager 主题管理器
 * 统一处理 light/dark/system 三态、持久化、事件分发与切换按钮绑定
 */
class ThemeManager {
    constructor() {
        this.storageKey = 'iatrode.theme.mode';
        this.cookieKey = 'iatrode_theme_mode';
        this.modes = ['system', 'light', 'dark'];
        this.systemMedia = window.matchMedia('(prefers-color-scheme: dark)');
        this.initialized = false;

        // 尽早应用，减少首屏闪动
        this.applyMode(this.getMode(), false);
    }

    normalizeMode(mode) {
        return this.modes.includes(mode) ? mode : 'system';
    }

    getMode() {
        if (window.IatrodeThemeStorage && typeof window.IatrodeThemeStorage.getMode === 'function') {
            return this.normalizeMode(window.IatrodeThemeStorage.getMode());
        }

        const cookieMode = this.getModeFromCookie();
        const localStorageMode = this.getModeFromLocalStorage();
        const mode = cookieMode || localStorageMode || 'system';

        if (cookieMode !== mode) {
            this.setModeCookie(mode);
        }
        if (localStorageMode !== mode) {
            this.setModeLocalStorage(mode);
        }

        return mode;
    }

    getModeFromLocalStorage() {
        try {
            return this.normalizeMode(localStorage.getItem(this.storageKey));
        } catch (e) {
            return null;
        }
    }

    getModeFromCookie() {
        try {
            const cookiePrefix = `${this.cookieKey}=`;
            const cookies = document.cookie ? document.cookie.split(';') : [];
            for (const rawCookie of cookies) {
                const cookie = rawCookie.trim();
                if (cookie.startsWith(cookiePrefix)) {
                    return this.normalizeMode(decodeURIComponent(cookie.slice(cookiePrefix.length)));
                }
            }
        } catch (e) { }

        return null;
    }

    getRegistrableDomain(hostname) {
        if (!hostname || hostname === 'localhost' || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) || hostname.includes(':')) {
            return null;
        }

        const parts = hostname.split('.').filter(Boolean);
        if (parts.length < 2) {
            return null;
        }

        if (parts.length >= 3) {
            const sld = parts[parts.length - 2];
            const tld = parts[parts.length - 1];
            const commonSecondLevelDomains = new Set(['com', 'net', 'org', 'gov', 'edu', 'ac', 'co']);
            if (tld.length === 2 && commonSecondLevelDomains.has(sld)) {
                return `.${parts.slice(-3).join('.')}`;
            }
        }

        return `.${parts.slice(-2).join('.')}`;
    }

    setModeCookie(mode) {
        try {
            const cookieParts = [
                `${this.cookieKey}=${encodeURIComponent(mode)}`,
                'Path=/',
                'Max-Age=31536000',
                'SameSite=Lax'
            ];

            const domain = this.getRegistrableDomain(window.location.hostname);
            if (domain) {
                cookieParts.push(`Domain=${domain}`);
            }

            if (window.location.protocol === 'https:') {
                cookieParts.push('Secure');
            }

            document.cookie = cookieParts.join('; ');
        } catch (e) { }
    }

    setModeLocalStorage(mode) {
        try {
            localStorage.setItem(this.storageKey, mode);
        } catch (e) { }
    }

    getResolvedTheme(mode) {
        if (mode === 'system') {
            return this.systemMedia.matches ? 'dark' : 'light';
        }

        return mode;
    }

    applyMode(mode, persist = true) {
        const normalizedMode = this.normalizeMode(mode);
        const resolvedTheme = this.getResolvedTheme(normalizedMode);

        document.documentElement.setAttribute('data-theme', resolvedTheme);
        document.documentElement.setAttribute('data-bs-theme', resolvedTheme);

        if (persist) {
            if (window.IatrodeThemeStorage && typeof window.IatrodeThemeStorage.setMode === 'function') {
                window.IatrodeThemeStorage.setMode(normalizedMode);
            } else {
                this.setModeCookie(normalizedMode);
                this.setModeLocalStorage(normalizedMode);
            }
        }

        this.updateToggleButtons(normalizedMode);
        document.dispatchEvent(new CustomEvent('theme-change', {
            detail: { mode: normalizedMode, theme: resolvedTheme }
        }));
    }

    getNextMode(mode) {
        const idx = this.modes.indexOf(this.normalizeMode(mode));
        return this.modes[(idx + 1) % this.modes.length];
    }

    updateToggleButtons(mode) {
        const fallbackTitleMap = {
            system: 'Theme: System',
            light: 'Theme: Light',
            dark: 'Theme: Dark'
        };
        const iconMap = {
            system: 'bi-circle-half',
            light: 'bi-sun',
            dark: 'bi-moon-stars'
        };

        document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
            const resolveLabel = (targetMode) => {
                const localized = button.getAttribute(`data-theme-label-${targetMode}`);
                return (localized && localized.trim()) ? localized : fallbackTitleMap[targetMode];
            };

            const title = resolveLabel(mode);
            button.setAttribute('data-theme-mode', mode);
            button.setAttribute('title', title);
            button.setAttribute('aria-label', title);
            button.setAttribute('data-tooltip', title);

            const icon = button.querySelector('i');
            if (icon) {
                icon.className = `bi ${iconMap[mode]}`;
                icon.setAttribute('aria-hidden', 'true');
            }
        });
    }

    bindToggleButtons() {
        document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
            if (button.dataset.themeToggleBound === 'true') return;

            button.dataset.themeToggleBound = 'true';
            button.addEventListener('click', () => {
                const nextMode = this.getNextMode(this.getMode());
                this.applyMode(nextMode, true);
            });
        });
    }

    initialize() {
        if (this.initialized) {
            this.bindToggleButtons();
            return;
        }

        this.initialized = true;
        this.bindToggleButtons();
        this.updateToggleButtons(this.getMode());

        const onSystemThemeChange = () => {
            if (this.getMode() === 'system') {
                this.applyMode('system', false);
            }
        };

        if (typeof this.systemMedia.addEventListener === 'function') {
            this.systemMedia.addEventListener('change', onSystemThemeChange);
        } else {
            this.systemMedia.addListener(onSystemThemeChange);
        }

        window.addEventListener('storage', (event) => {
            if (event.key === this.storageKey) {
                this.applyMode(this.getMode(), false);
            }
        });

        if (window.MutationObserver) {
            const observer = new MutationObserver(() => this.bindToggleButtons());
            observer.observe(document.body, { childList: true, subtree: true });
        }
    }
}

// 全局主题管理器实例（保证同页只创建一次）
window.themeManager = window.themeManager || new ThemeManager();

// 初始化所有组件
function initializeComponents() {
    // 创建/引用全局 Toast/Alert/FormInputUtils（现代模块负责功能）
    window.toastInstance = (globalThis.Toast && globalThis.Toast._instance) ? globalThis.Toast : (window.Toast || null);
    window.alertInstance = (globalThis.Alert && globalThis.Alert._instance) ? globalThis.Alert : (window.Alert || null);
    window.formInputUtils = window.FormInputUtils || window.FormInputUtils || null;
    window.fieldSelectInstance = new FieldSelectComponent();
    window.methodOptionInstance = new MethodOptionComponent();
    window.loadingButtonInstance = new LoadingButtonComponent();
    window.triStateCheckboxInstance = new TriStateCheckboxComponent(); // 添加三态复选框实例
    if (window.themeManager && typeof window.themeManager.initialize === 'function') {
        window.themeManager.initialize();
    }
}

// 在 DOM 准备就绪时初始化
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponents);
} else {
    // DOM 已经准备就绪
    initializeComponents();
}

// 便捷的全局辅助方法（使用 Alert/Toast 实例）
// Convenience: expose simple global helpers that route to Alert/Toast
window.showSuccess = (message, options) => (window.alertInstance || globalThis.Alert || window.Alert)?.success(message, options);
window.showError = (message, options) => (window.alertInstance || globalThis.Alert || window.Alert)?.error(message, options);
window.showWarning = (message, options) => (window.alertInstance || globalThis.Alert || window.Alert)?.warning(message, options);
window.showInfo = (message, options) => (window.alertInstance || globalThis.Alert || window.Alert)?.info(message, options);

// 输入框错误提示便捷方法
// Input validation helpers — route to FormInputUtils (modern)
window.showInputError = (input, message, options) => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.showInputError(input, message, options);
window.showInputSuccess = (input, message, options) => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.showInputSuccess(input, message, options);
window.showInputWarning = (input, message, options) => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.showInputWarning(input, message, options);
window.showInputInfo = (input, message, options) => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.showInputInfo(input, message, options);
window.showInputLoading = (input, message, options) => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.showInputLoading(input, message, options);
window.hideInputError = (input) => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.hideInputError(input);
window.clearAllInputErrors = () => (window.formInputUtils || window.FormInputUtils || globalThis.FormInputUtils)?.clearInputValidation?.(document) || null;

// 公共 API 方法
window.setButtonLoading = (button, loadingText) => {
    if (window.loadingButtonInstance) {
        window.loadingButtonInstance.setLoading(button, loadingText);
    }
};

window.removeButtonLoading = (button) => {
    if (window.loadingButtonInstance) {
        window.loadingButtonInstance.removeLoading(button);
    }
};

window.getFieldSelectValue = (selectElement) => {
    if (window.fieldSelectInstance) {
        return window.fieldSelectInstance.getValue(selectElement);
    }
    return null;
};

window.setFieldSelectValue = (selectElement, value) => {
    if (window.fieldSelectInstance) {
        return window.fieldSelectInstance.setValue(selectElement, value);
    }
    return false;
};

// jQuery 扩展方法
// 检查 jQuery 是否可用
if (typeof jQuery !== 'undefined') {
    (function ($) {

        /**
         * 按钮加载状态相关的 jQuery 扩展方法
         */

        // 设置按钮加载状态
        $.fn.setButtonLoading = function (loadingText) {
            return this.each(function () {
                if (window.loadingButtonInstance) {
                    window.loadingButtonInstance.setLoading(this, loadingText);
                }
            });
        };

        // 移除按钮加载状态
        $.fn.removeButtonLoading = function () {
            return this.each(function () {
                if (window.loadingButtonInstance) {
                    window.loadingButtonInstance.removeLoading(this);
                }
            });
        };

        // 切换按钮加载状态
        $.fn.toggleButtonLoading = function (loadingText) {
            return this.each(function () {
                if (this.classList.contains('btn-loading')) {
                    if (window.loadingButtonInstance) {
                        window.loadingButtonInstance.removeLoading(this);
                    }
                } else {
                    if (window.loadingButtonInstance) {
                        window.loadingButtonInstance.setLoading(this, loadingText);
                    }
                }
            });
        };

        /**
         * FieldSelect 下拉选择相关的 jQuery 扩展方法
         */

        // 获取下拉选择的值
        $.fn.getFieldSelectValue = function () {
            if (this.length > 0 && window.fieldSelectInstance) {
                return window.fieldSelectInstance.getValue(this[0]);
            }
            return null;
        };

        // 设置下拉选择的值
        $.fn.setFieldSelectValue = function (value) {
            return this.each(function () {
                if (window.fieldSelectInstance) {
                    window.fieldSelectInstance.setValue(this, value);
                }
            });
        };

        // 打开下拉选择
        $.fn.openFieldSelect = function () {
            return this.each(function () {
                if (window.fieldSelectInstance) {
                    window.fieldSelectInstance.open(this);
                }
            });
        };

        // 关闭下拉选择
        $.fn.closeFieldSelect = function () {
            return this.each(function () {
                if (window.fieldSelectInstance) {
                    window.fieldSelectInstance.close(this);
                }
            });
        };

        // 切换下拉选择状态
        $.fn.toggleFieldSelect = function () {
            return this.each(function () {
                if (window.fieldSelectInstance) {
                    window.fieldSelectInstance.toggle(this);
                }
            });
        };

        // 添加 loading 类和动画
        $.fn.addLoading = function (text) {
            return this.each(function () {
                if (window.loadingButtonInstance) {
                    window.loadingButtonInstance.setLoading(this, text || undefined);
                }
            });
        };

        // 移除 loading 状态
        $.fn.removeLoading = function () {
            return this.each(function () {
                if (window.loadingButtonInstance) {
                    window.loadingButtonInstance.removeLoading(this);
                }
            });
        };

        // 设置元素为禁用状态
        $.fn.setDisabled = function (disabled = true) {
            return this.each(function () {
                if (disabled) {
                    $(this).addClass('disabled').attr('disabled', true);
                } else {
                    $(this).removeClass('disabled').removeAttr('disabled');
                }
            });
        };

        // 切换禁用状态
        $.fn.toggleDisabled = function () {
            return this.each(function () {
                const $this = $(this);
                if ($this.hasClass('disabled') || $this.prop('disabled')) {
                    $this.setDisabled(false);
                } else {
                    $this.setDisabled(true);
                }
            });
        };

        // 添加或移除 active 类
        $.fn.setActive = function (active = true) {
            return this.each(function () {
                if (active) {
                    $(this).addClass('active');
                } else {
                    $(this).removeClass('active');
                }
            });
        };

        // 切换 active 状态
        $.fn.toggleActive = function () {
            return this.each(function () {
                $(this).toggleClass('active');
            });
        };

        // 平滑滚动到元素
        $.fn.smoothScrollTo = function (duration = 500) {
            if (this.length > 0) {
                $('html, body').animate({
                    scrollTop: this.offset().top
                }, duration);
            }
            return this;
        };

        // 淡入显示
        $.fn.fadeInShow = function (duration = 300) {
            return this.each(function () {
                $(this).css({
                    opacity: 0,
                    display: 'block'
                }).animate({
                    opacity: 1
                }, duration);
            });
        };
        // 淡出隐藏
        $.fn.fadeOutHide = function (duration = 300) {
            return this.each(function () {
                $(this).animate({
                    opacity: 0
                }, duration, function () {
                    $(this).hide();
                });
            });
        };
        // 输入框错误提示
        $.fn.showInputError = function (message, options) {
            return this.each(function () {
                const impl = window.FormInputUtils || window.formInputUtils || null;
                if (impl && typeof impl.showInputError === 'function') impl.showInputError(this, message, options);
            });
        };

        $.fn.showInputSuccess = function (message, options) {
            return this.each(function () {
                const impl = window.FormInputUtils || window.formInputUtils || null;
                if (impl && typeof impl.showInputSuccess === 'function') impl.showInputSuccess(this, message, options);
            });
        };

        $.fn.showInputWarning = function (message, options) {
            return this.each(function () {
                const impl = window.FormInputUtils || window.formInputUtils || null;
                if (impl && typeof impl.showInputWarning === 'function') impl.showInputWarning(this, message, options);
            });
        };

        $.fn.showInputInfo = function (message, options) {
            return this.each(function () {
                const impl = window.FormInputUtils || window.formInputUtils || null;
                if (impl && typeof impl.showInputInfo === 'function') impl.showInputInfo(this, message, options);
            });
        };

        $.fn.showInputLoading = function (message, options) {
            return this.each(function () {
                const impl = window.FormInputUtils || window.formInputUtils || null;
                if (impl && typeof impl.showInputLoading === 'function') impl.showInputLoading(this, message, options);
            });
        };

        $.fn.hideInputError = function () {
            return this.each(function () {
                const impl = window.FormInputUtils || window.formInputUtils || null;
                if (impl && typeof impl.hideInputError === 'function') impl.hideInputError(this);
            });
        };

    })(jQuery);
} else {
    console.warn('jQuery not found, jQuery extensions not available');
}

/**
 * TriStateCheckboxComponent 三态复选框组件类
 * 提供三态复选框功能（未选中 -> 中间态 -> 选中 -> 循环）
 */
class TriStateCheckboxComponent {
    constructor() {
        this.init();
    }

    init() {
        this.bindTriStateCheckboxes();
        this.observeNewElements();
    }

    /**
     * 绑定所有三态复选框
     */
    bindTriStateCheckboxes() {
        const triStateCheckboxes = document.querySelectorAll('.input-checkbox.tri-state');
        triStateCheckboxes.forEach(checkbox => {
            if (!checkbox.hasAttribute('data-tri-state-bound')) {
                this.bindTriStateCheckbox(checkbox);
                checkbox.setAttribute('data-tri-state-bound', 'true');
            }
        });
    }

    /**
     * 绑定单个三态复选框
     * @param {HTMLElement} checkboxContainer - 复选框容器
     */
    bindTriStateCheckbox(checkboxContainer) {
        const input = checkboxContainer.querySelector('input[type="checkbox"]');
        if (!input) return;

        // 初始化三态状态
        if (!input.checked) {
            // 如果初始状态是未选中，首次点击将变为indeterminate状态
            input.classList.add('tri-state-unchecked');
            this.setIndeterminateState(checkboxContainer, false);
        } else {
            // 如果初始状态是选中，则保持选中状态
            input.classList.add('tri-state-checked');
        }

        // 添加点击事件处理
        checkboxContainer.addEventListener('click', (e) => {
            this.toggleState(checkboxContainer);
            e.preventDefault(); // 阻止默认行为
        });

        // 防止原生checkbox事件干扰三态行为
        input.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        input.addEventListener('change', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
    }

    /**
     * 切换三态复选框状态
     * @param {HTMLElement|String} checkbox - 复选框容器元素或选择器
     */
    toggleState(checkbox) {
        const container = typeof checkbox === 'string' ? document.querySelector(checkbox) : checkbox;
        if (!container || !container.classList.contains('tri-state')) return;

        const input = container.querySelector('input[type="checkbox"]');
        if (!input) return;

        // 获取当前状态
        // 三种状态: unchecked -> indeterminate -> checked -> unchecked
        let hasIndeterminate = container.classList.contains('indeterminate');
        let isChecked = input.checked;

        // 切换到下一个状态
        if (!hasIndeterminate && !isChecked) {
            // 未选中 -> 中间态
            this.setIndeterminateState(container, true);
            input.indeterminate = true;
            input.checked = false;
            container.classList.add('indeterminate');
            input.classList.remove('tri-state-unchecked');
            input.classList.add('tri-state-indeterminate');

            // 触发自定义三态变化事件
            this.triggerChangeEvent(container, 'indeterminate');

        } else if (hasIndeterminate) {
            // 中间态 -> 选中
            this.setIndeterminateState(container, false);
            input.indeterminate = false;
            input.checked = true;
            container.classList.remove('indeterminate');
            input.classList.remove('tri-state-indeterminate');
            input.classList.add('tri-state-checked');

            // 触发自定义三态变化事件
            this.triggerChangeEvent(container, 'checked');

        } else if (isChecked) {
            // 选中 -> 未选中
            input.indeterminate = false;
            input.checked = false;
            container.classList.remove('indeterminate');
            input.classList.remove('tri-state-checked');
            input.classList.add('tri-state-unchecked');

            // 触发自定义三态变化事件
            this.triggerChangeEvent(container, 'unchecked');
        }
    }

    /**
     * 设置中间态样式
     * @param {HTMLElement} container - 复选框容器
     * @param {boolean} isIndeterminate - 是否为中间态
     */
    setIndeterminateState(container, isIndeterminate) {
        if (isIndeterminate) {
            container.classList.add('indeterminate');
        } else {
            container.classList.remove('indeterminate');
        }
    }

    /**
     * 触发自定义三态变化事件
     * @param {HTMLElement} container - 复选框容器
     * @param {string} state - 当前状态
     */
    triggerChangeEvent(container, state) {
        const input = container.querySelector('input[type="checkbox"]');
        if (!input) return;

        // 创建并分发自定义事件
        const event = new CustomEvent('tri-state-change', {
            detail: {
                state: state,
                input: input,
                container: container
            },
            bubbles: true
        });
        container.dispatchEvent(event);
    }

    /**
     * 获取当前状态
     * @param {HTMLElement|String} checkbox - 复选框容器或选择器
     * @returns {String} - 'unchecked', 'indeterminate', 'checked'
     */
    getState(checkbox) {
        const container = typeof checkbox === 'string' ? document.querySelector(checkbox) : checkbox;
        if (!container || !container.classList.contains('tri-state')) return null;

        const input = container.querySelector('input[type="checkbox"]');
        if (!input) return null;

        if (container.classList.contains('indeterminate')) return 'indeterminate';
        return input.checked ? 'checked' : 'unchecked';
    }

    /**
     * 设置特定状态
     * @param {HTMLElement|String} checkbox - 复选框容器或选择器
     * @param {String} state - 要设置的状态: 'unchecked', 'indeterminate', 'checked'
     */
    setState(checkbox, state) {
        const container = typeof checkbox === 'string' ? document.querySelector(checkbox) : checkbox;
        if (!container || !container.classList.contains('tri-state')) return;

        const input = container.querySelector('input[type="checkbox"]');
        if (!input) return;

        switch (state) {
            case 'unchecked':
                input.indeterminate = false;
                input.checked = false;
                container.classList.remove('indeterminate');
                input.classList.remove('tri-state-indeterminate', 'tri-state-checked');
                input.classList.add('tri-state-unchecked');
                this.triggerChangeEvent(container, 'unchecked');
                break;

            case 'indeterminate':
                input.indeterminate = true;
                input.checked = false;
                container.classList.add('indeterminate');
                input.classList.remove('tri-state-unchecked', 'tri-state-checked');
                input.classList.add('tri-state-indeterminate');
                this.triggerChangeEvent(container, 'indeterminate');
                break;

            case 'checked':
                input.indeterminate = false;
                input.checked = true;
                container.classList.remove('indeterminate');
                input.classList.remove('tri-state-unchecked', 'tri-state-indeterminate');
                input.classList.add('tri-state-checked');
                this.triggerChangeEvent(container, 'checked');
                break;
        }
    }

    /**
     * 监听动态添加的元素
     */
    observeNewElements() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // 检查节点本身是否是三态复选框
                        if (node.classList &&
                            node.classList.contains('input-checkbox') &&
                            node.classList.contains('tri-state') &&
                            !node.hasAttribute('data-tri-state-bound')) {
                            this.bindTriStateCheckbox(node);
                        }

                        // 检查子节点中的三态复选框
                        const triStateCheckboxes = node.querySelectorAll ?
                            node.querySelectorAll('.input-checkbox.tri-state:not([data-tri-state-bound])') : [];
                        triStateCheckboxes.forEach(checkbox => {
                            this.bindTriStateCheckbox(checkbox);
                        });
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// =============================================================================
// 工具函数
// =============================================================================

/**
 * 全局唯一ID生成器
 * @returns {string} 唯一ID
 */
function generateUniqueId() {
    return 'id-' + Math.random().toString(36).substr(2, 16);
}

/**
 * 深拷贝
 * @param {Object} obj - 源对象
 * @returns {Object} 拷贝后的新对象
 */
function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * 合并对象
 * @param {...Object} sources - 源对象
 * @returns {Object} 合并后的新对象
 */
function mergeObjects(...sources) {
    return Object.assign({}, ...sources);
}

/**
 * Dropdown Core
 * 统一下拉菜单的定位与显隐逻辑，供 avatar/button 等组件复用
 */
const dropdownCore = (() => {
    const DEFAULTS = {
        placement: 'auto',
        offset: 8,
        viewportMargin: 16,
        appendToBody: true,
        visible: false,
        preferEndOnRight: true
    };

    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

    const getRect = (anchor) => {
        if (!anchor) return null;
        if (typeof anchor.getBoundingClientRect === 'function') {
            return anchor.getBoundingClientRect();
        }
        if (typeof anchor === 'object' && typeof anchor.left === 'number') {
            return anchor;
        }
        return null;
    };

    const buildCandidates = (rect, width, height, options) => {
        const gap = options.offset;
        const base = [
            { name: 'bottom-end', left: rect.right - width, top: rect.bottom + gap, priority: 0 },
            { name: 'top-end', left: rect.right - width, top: rect.top - height - gap, priority: 1 },
            { name: 'bottom-start', left: rect.left, top: rect.bottom + gap, priority: 2 },
            { name: 'top-start', left: rect.left, top: rect.top - height - gap, priority: 3 }
        ];

        const placement = String(options.placement || 'auto').toLowerCase();
        if (placement !== 'auto') {
            return base
                .filter((candidate) => candidate.name === placement)
                .concat(base.filter((candidate) => candidate.name !== placement));
        }

        if (!options.preferEndOnRight) {
            return base;
        }

        const shouldPreferEnd = (rect.left + rect.width / 2) > (window.innerWidth / 2);
        if (shouldPreferEnd) return base;

        return [
            { name: 'bottom-start', left: rect.left, top: rect.bottom + gap, priority: 0 },
            { name: 'top-start', left: rect.left, top: rect.top - height - gap, priority: 1 },
            { name: 'bottom-end', left: rect.right - width, top: rect.bottom + gap, priority: 2 },
            { name: 'top-end', left: rect.right - width, top: rect.top - height - gap, priority: 3 }
        ];
    };

    const applyDirectionClasses = (menuEl, placementName) => {
        if (!menuEl || !placementName) return;

        const [vertical, horizontal] = placementName.split('-');
        const verticalState = vertical === 'top' ? 'up' : 'down';
        const horizontalState = horizontal === 'start' ? 'left' : 'right';
        const verticalLegacy = verticalState === 'up' ? 'dropdown-bottom' : 'dropdown-top';
        const horizontalLegacy = horizontalState === 'left' ? 'dropdown-left' : 'dropdown-right';

        menuEl.classList.remove(
            'dropdown-top', 'dropdown-bottom', 'dropdown-left', 'dropdown-right',
            'dropdown-up', 'dropdown-down', 'dropdown-start', 'dropdown-end'
        );
        menuEl.classList.add(
            verticalLegacy,
            horizontalLegacy,
            `dropdown-${verticalState}`,
            `dropdown-${horizontal === 'start' ? 'start' : 'end'}`
        );
    };

    const place = (menuEl, anchor, opts = {}) => {
        if (!menuEl) return null;

        const options = Object.assign({}, DEFAULTS, opts);
        const anchorRect = getRect(anchor);
        if (!anchorRect) return null;

        if (options.appendToBody && menuEl.parentElement !== document.body) {
            document.body.appendChild(menuEl);
        }

        menuEl.style.setProperty('display', 'block', 'important');
        menuEl.style.setProperty('visibility', 'hidden', 'important');
        menuEl.style.setProperty('position', 'fixed', 'important');
        menuEl.style.setProperty('left', '0', 'important');
        menuEl.style.setProperty('top', '0', 'important');
        menuEl.style.setProperty('right', 'auto', 'important');

        const measuredRect = menuEl.getBoundingClientRect();
        const menuWidth = measuredRect.width;
        const menuHeight = measuredRect.height;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = options.viewportMargin;

        const overflowScore = (left, top) => {
            const right = left + menuWidth;
            const bottom = top + menuHeight;
            return (
                Math.max(0, margin - left) +
                Math.max(0, right - (viewportWidth - margin)) +
                Math.max(0, margin - top) +
                Math.max(0, bottom - (viewportHeight - margin))
            );
        };

        const candidates = buildCandidates(anchorRect, menuWidth, menuHeight, options);
        let best = candidates[0];
        let bestScore = Number.POSITIVE_INFINITY;

        candidates.forEach((candidate) => {
            const score = overflowScore(candidate.left, candidate.top);
            if (score < bestScore || (score === bestScore && candidate.priority < best.priority)) {
                best = candidate;
                bestScore = score;
            }
        });

        const maxLeft = Math.max(margin, viewportWidth - menuWidth - margin);
        const maxTop = Math.max(margin, viewportHeight - menuHeight - margin);
        const left = clamp(best.left, margin, maxLeft);
        const top = clamp(best.top, margin, maxTop);

        menuEl.style.setProperty('left', `${Math.round(left)}px`, 'important');
        menuEl.style.setProperty('top', `${Math.round(top)}px`, 'important');
        menuEl.style.setProperty('right', 'auto', 'important');
        menuEl.style.setProperty('bottom', 'auto', 'important');
        menuEl.style.setProperty('transform', 'translateY(0)', 'important');

        applyDirectionClasses(menuEl, best.name);

        if (options.visible) {
            menuEl.style.setProperty('opacity', '1', 'important');
            menuEl.style.setProperty('visibility', 'visible', 'important');
        }

        return { left, top, placement: best.name };
    };

    const show = (menuEl) => {
        if (!menuEl) return;
        menuEl.style.setProperty('display', 'block', 'important');
        menuEl.style.setProperty('opacity', '1', 'important');
        menuEl.style.setProperty('visibility', 'visible', 'important');
        menuEl.style.setProperty('transform', 'translateY(0)', 'important');
        menuEl.classList.add('show');
    };

    const hide = (menuEl, options = {}) => {
        if (!menuEl) return;
        const keepDisplay = options.keepDisplay === true;
        menuEl.style.setProperty('opacity', '0', 'important');
        menuEl.style.setProperty('visibility', 'hidden', 'important');
        menuEl.style.setProperty('transform', 'translateY(-10px)', 'important');
        if (!keepDisplay) {
            menuEl.style.setProperty('display', 'none', 'important');
        }
        menuEl.classList.remove('show');
    };

    return { place, show, hide };
})();

window.IatrodeUI = window.IatrodeUI || {};
window.IatrodeUI.dropdown = dropdownCore;

// 全局工具函数
window.utils = {
    generateUniqueId,
    deepClone,
    mergeObjects
};
